import {forwardRef, useEffect, createContext, useMemo, useCallback} from 'react';
import _ from 'lodash';
import {Grid} from "@mui/material";
import ColumnEvents from './calendarTiles/columnEvents';
import NewAvailableLockSlots from './calendarTiles/availableLockSlots';
import PatientAssignableSlots from './calendarTiles/patientAssignableSlots';
import {NewLockedSlotTiles} from './calendarTiles/lockedSlots';
import EdReviewTiles from './calendarTiles/edReview';
import {getOptionAvailableOptions, getReviewData, SLOT_MANIPULATION_OPTIONS} from '../../../../utils/edReviewUtils';
import {useDispatch, useSelector} from 'react-redux';
import {
  allBarsReviews,
  barsContextActive,
  dayCalendarColumns,
  isDischargeContext,
  selectedDate as currentSelectedDate,
  setSlotManipulationState,
} from '../../../../../../../../store/reducers/reviews';
import {TemporaryLockedSlot} from './calendarTiles/temporaryLockedSlot';
import moment from 'moment';
import BarsLockedSlots from './calendarTiles/barsLockedSlots';

const DailyCalendarColumn = ({
    OFFSET_LEFT,
    onSelectTime,
    getEventIndex,
    columnBarsSlots,
    date, selectPatient,
    dailySlots, reviewsToday,
    columnWidth, lockedSlots,
    columnEvents, columnIndex,
}) => {

  return (
    <Grid
      item
      style={{
        flexBasis: columnWidth,
        position: 'relative',
      }}
    >
      <ColumnEvents
        date={date}
        colWidth="100%"
        events={columnEvents}
        onPatientSelect={selectPatient}
      />

      <NewAvailableLockSlots
        colWidth="100%"
        columnIndex={columnIndex}
        onSelectTime={onSelectTime}
      />

      <PatientAssignableSlots
        colWidth='100%'
        dailySlots={dailySlots}
        leftOffset={OFFSET_LEFT}
        eventIndex={columnIndex}
        columnIndex={columnIndex}
        onSelectTime={onSelectTime}
        findEventIndex={getEventIndex}
      />

      <NewLockedSlotTiles
        colWidth="100%"
        slots={lockedSlots}
        leftOffset={OFFSET_LEFT}
        eventIndex={columnIndex}
        onSelectTime={onSelectTime}
        findEventIndex={getEventIndex}
        columnIndex={columnIndex}
      />

      <TemporaryLockedSlot
        colWidth="100%"
        eventIndex={columnIndex}
        columnEvents={columnEvents}
        onSelectTime={onSelectTime}
      />

      <EdReviewTiles
        colWidth="100%"
        isDraggable={true}
        reviews={reviewsToday}
        leftOffset={OFFSET_LEFT}
        onSelectPatient={selectPatient}
        findEventIndex={getEventIndex}
      />

      {!_.isEmpty(columnBarsSlots) && (
        <BarsLockedSlots
          date={date}
          colWidth="100%"
          events={columnBarsSlots}
        />
      )}
    </Grid>
  )
};

const DailyCalendarColumns = forwardRef((
  {
    temporaryEditedSessions,
    OFFSET_LEFT,
    getEventIndex,
    date,
    events,
    selectPatient,
    dailySlots,
    reviewsToday,
    columnWidth,
    droppableStyle,
    onSelectTime,
  }, ref) => {
  const dispatch = useDispatch();

  const barsReviews = useSelector(allBarsReviews);
  const calendarDate = useSelector(currentSelectedDate);
  const dischargeContext = useSelector(isDischargeContext);
  const barsCtxActive = useSelector(barsContextActive);
  const dayCalendarColumnsState = useSelector(dayCalendarColumns);

  const getColumnBarsSlots = useCallback((eventsArr) => {
    if (!barsCtxActive) return [];

    const evts = [];

    eventsArr.forEach((sessionEvent) => {
      if (!_.isEmpty(barsReviews)) {
        const assigned =  _.filter(barsReviews, slot => {
          return slot?.start && slot?.assignedSession === sessionEvent?.sessionId && moment(slot?.start).isSame(moment(date, "DD/MM/YYYY"), 'day')
        });

        if  (!_.isEmpty(assigned)) {
          evts.push(...assigned);
        }
      }
    })

    return evts;
  }, [barsCtxActive, temporaryEditedSessions, barsReviews])

  useEffect(() => {
    if (!!dischargeContext) {
      dispatch(
        setSlotManipulationState({
          option: SLOT_MANIPULATION_OPTIONS.ASSIGN,
          availableOptions: getOptionAvailableOptions(
            SLOT_MANIPULATION_OPTIONS.ASSIGN,
            dayCalendarColumnsState,
            calendarDate,
            reviewsToday
          )
        })
      );
    }
  }, [dischargeContext, dayCalendarColumnsState.length, calendarDate, reviewsToday.length]);

    return temporaryEditedSessions.map((sessionColumn, columnIndex) => {
      const columnEvents = sessionColumn.events;

      const columnEventsSessionIds = columnEvents.reduce((acc, current) => {
        if (current.sessionId !== undefined) {
          acc.push(current.sessionId);
        }
        return acc;
      }, []);

      const columnBarsSlots = getColumnBarsSlots(columnEvents);
      const lockedSlots = _.filter(columnEvents, (ev) => !!ev?.locked);

      const patientReviews = _.filter(
        reviewsToday,
        (patient) => columnEventsSessionIds.includes(getReviewData(patient)?.assignedSession)
      );

      return (
        <DailyCalendarColumn
          ref={ref}
          date={date}
          events={events}
          dailySlots={dailySlots}
          key={sessionColumn.colId}
          lockedSlots={lockedSlots}
          OFFSET_LEFT={OFFSET_LEFT}
          columnIndex={columnIndex}
          columnWidth={columnWidth}
          onSelectTime={onSelectTime}
          columnEvents={columnEvents}
          reviewsToday={patientReviews}
          getEventIndex={getEventIndex}
          selectPatient={selectPatient}
          columnBarsSlots={columnBarsSlots}
        />
      )
    })
});

export default DailyCalendarColumns;